import React, { Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AppLayout from "./components/layout/AppLayout";
import ProtectedRoute from "./pages/ProtectedRoute";
import ComingSoon from "./components/ComingSoon";
import PermGuard, { firstAllowedPath } from "./pages/PermGuard";
import { lazyPage, prefetchPages } from "./lib/lazyPage";

const Login = lazyPage(() => import("./pages/Login"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const Customers = lazyPage(() => import("./pages/Customers"));
const CRMCleanup = lazyPage(() => import("./pages/CRMCleanup"));
const MESPlanning = lazyPage(() => import("./pages/mes/ProductionPlanning"));
const EmployeeMaster = lazyPage(() => import("./pages/mes/EmployeeMaster"));
const MesApprovals = lazyPage(() => import("./pages/mes/MesApprovals"));
const MesRegister = lazyPage(() => import("./pages/mes/MesRegister"));
const MesDashboard = lazyPage(() => import("./pages/mes/MesDashboard"));
const FinishedGoods = lazyPage(() => import("./pages/mes/FinishedGoods"));
const OperatorLogin = lazyPage(() => import("./pages/mes/OperatorLogin"));
const OperatorJobs = lazyPage(() => import("./pages/mes/OperatorJobs"));
const OperatorJobExecution = lazyPage(() => import("./pages/mes/OperatorJobExecution"));
const CustomerDetail = lazyPage(() => import("./pages/CustomerDetail"));
const Contacts = lazyPage(() => import("./pages/Contacts"));
const Products = lazyPage(() => import("./pages/Products"));
const ProductDetail = lazyPage(() => import("./pages/ProductDetail"));
const CompoundCosting = lazyPage(() => import("./pages/CompoundCosting"));
const ProductCosting = lazyPage(() => import("./pages/ProductCosting"));
const Enquiries = lazyPage(() => import("./pages/Enquiries"));
const QuoteGenerator = lazyPage(() => import("./pages/QuoteGenerator"));
const QuoteRegister = lazyPage(() => import("./pages/QuoteRegister"));
const PIRegister = lazyPage(() => import("./pages/PIRegister"));
const TaxInvoices = lazyPage(() => import("./pages/TaxInvoices"));
const Accounts = lazyPage(() => import("./pages/Accounts"));
const EInvoiceQueue = lazyPage(() => import("./pages/EInvoiceQueue"));
const Followups = lazyPage(() => import("./pages/Followups"));
const Reports = lazyPage(() => import("./pages/Reports"));
const Notes = lazyPage(() => import("./pages/Notes"));
const Settings = lazyPage(() => import("./pages/Settings"));
const SalesOrders = lazyPage(() => import("./pages/SalesOrders"));
const SalesOrderDetail = lazyPage(() => import("./pages/SalesOrderDetail"));
const OrderTracking = lazyPage(() => import("./pages/OrderTracking"));
const EmailDrafts = lazyPage(() => import("./pages/EmailDrafts"));
const TallyPairing = lazyPage(() => import("./pages/TallyPairing"));
const OrderTrackingDetail = lazyPage(() => import("./pages/OrderTrackingDetail"));
const WorkOrders = lazyPage(() => import("./pages/WorkOrders"));
const WorkOrderDetail = lazyPage(() => import("./pages/WorkOrderDetail"));
const ProductionDashboard = lazyPage(() => import("./pages/ProductionDashboard"));
const PlantDashboard = lazyPage(() => import("./pages/PlantDashboard"));
const RecipeMaster = lazyPage(() => import("./pages/RecipeMaster"));
const RawMaterialMaster = lazyPage(() => import("./pages/RawMaterialMaster"));
const MouldMaster = lazyPage(() => import("./pages/MouldMaster"));
const ScanDie = lazyPage(() => import("./pages/ScanDie"));
const BoxPage = lazyPage(() => import("./pages/BoxPage"));
const ScanLoad = lazyPage(() => import("./pages/ScanLoad"));
const BoxTrace = lazyPage(() => import("./pages/BoxTrace"));
const StickerPhotos = lazyPage(() => import("./pages/StickerPhotos"));
const CustomerItemNames = lazyPage(() => import("./pages/CustomerItemNames"));
const MachineMaster = lazyPage(() => import("./pages/MachineMaster"));
const MachineDetail = lazyPage(() => import("./pages/MachineDetail"));
const MaintenanceDashboard = lazyPage(() => import("./pages/MaintenanceDashboard"));
const Breakdowns = lazyPage(() => import("./pages/Breakdowns"));
const PMSchedule = lazyPage(() => import("./pages/PMSchedule"));
const ProcurementDashboard = lazyPage(() => import("./pages/ProcurementDashboard"));
const MaterialReceipts = lazyPage(() => import("./pages/MaterialReceipts"));
const NewGRN = lazyPage(() => import("./pages/NewGRN"));
const GRNDetail = lazyPage(() => import("./pages/GRNDetail"));
const Vendors = lazyPage(() => import("./pages/Vendors"));
const RMStock = lazyPage(() => import("./pages/RMStock"));
const InventoryDashboard = lazyPage(() => import("./pages/InventoryDashboard"));
const RawMaterialInventory = lazyPage(() => import("./pages/RawMaterialInventory"));
const CompoundProductionEntry = lazyPage(() => import("./pages/CompoundProductionEntry"));
const ProductionRegister = lazyPage(() => import("./pages/ProductionRegister"));
const InventoryLedger = lazyPage(() => import("./pages/InventoryLedger"));
const StockTransfers = lazyPage(() => import("./pages/StockTransfers"));
const PurchaseRecommendations = lazyPage(() => import("./pages/PurchaseRecommendations"));
const PhysicalStock = lazyPage(() => import("./pages/PhysicalStock"));
const ImportWizard = lazyPage(() => import("./pages/ImportWizard"));
const ProductionPlanning = lazyPage(() => import("./pages/ProductionPlanning"));
const CompoundBatches = lazyPage(() => import("./pages/CompoundBatches"));
const ProductionJobs = lazyPage(() => import("./pages/ProductionJobs"));
const UserManagement = lazyPage(() => import("./pages/UserManagement"));
const AuditLog = lazyPage(() => import("./pages/AuditLog"));
const Dispatch = lazyPage(() => import("./pages/Dispatch"));
const AIProductFinder = lazyPage(() => import("./pages/AIProductFinder"));
const AIWeightCalculator = lazyPage(() => import("./pages/AIWeightCalculator"));
const AIToolingEngineer = lazyPage(() => import("./pages/AIToolingEngineer"));
const Landing = lazyPage(() => import("./pages/Landing"));
const DemoComingSoon = lazyPage(() => import("./pages/DemoComingSoon"));
const DemoLauncher = lazyPage(() => import("./pages/DemoLauncher"));
const WorkspacePortal = lazyPage(() => import("./pages/WorkspacePortal"));
const ToolingDashboard = lazyPage(() => import("./pages/ToolingDashboard"));
const ToolingRequestDetail = lazyPage(() => import("./pages/ToolingRequestDetail"));
const PurchaseIndents = lazyPage(() => import("./pages/PurchaseIndents"));
const FcChallanList = lazyPage(() => import("./pages/fc/ChallanList"));
const FcChallanForm = lazyPage(() => import("./pages/fc/ChallanForm"));
const FcChallanDetail = lazyPage(() => import("./pages/fc/ChallanDetail"));
const FcInwardForm = lazyPage(() => import("./pages/fc/InwardForm"));
const FcReports = lazyPage(() => import("./pages/fc/Reports"));
const FcMasters = lazyPage(() => import("./pages/fc/Masters"));
const QcHome = lazyPage(() => import("./pages/qc/QcHome"));
const QcDispatch = lazyPage(() => import("./pages/qc/QcDispatch"));
const QcInspection = lazyPage(() => import("./pages/qc/QcInspection"));
const QcTemplates = lazyPage(() => import("./pages/qc/QcTemplates"));
const QcTemplateEdit = lazyPage(() => import("./pages/qc/QcTemplateEdit"));
const QcRegister = lazyPage(() => import("./pages/qc/QcRegister"));
const QcSettings = lazyPage(() => import("./pages/qc/QcSettings"));
const QcStageList = lazyPage(() => import("./pages/qc/QcStageList"));
const QcStageForm = lazyPage(() => import("./pages/qc/QcStageForm"));
const QcDashboard = lazyPage(() => import("./pages/qc/QcDashboard"));
const QcNightReview = lazyPage(() => import("./pages/qc/QcNightReview"));
const QcPatrolBoard = lazyPage(() => import("./pages/qc/QcPatrol").then((m) => ({ default: m.QcPatrolBoard })));
const QcPatrolForm = lazyPage(() => import("./pages/qc/QcPatrol").then((m) => ({ default: m.QcPatrolForm })));
const CapaList = lazyPage(() => import("./pages/capa/CapaList"));
const CapaNew = lazyPage(() => import("./pages/capa/CapaNew"));
const CapaDetail = lazyPage(() => import("./pages/capa/CapaDetail"));
const CapaTemplates = lazyPage(() => import("./pages/capa/CapaTemplates"));
const CapaTemplateEdit = lazyPage(() => import("./pages/capa/CapaTemplateEdit"));
const DdHome = lazyPage(() => import("./pages/dd/DdHome"));
const DdUpload = lazyPage(() => import("./pages/dd/DdUpload"));
const DdDocuments = lazyPage(() => import("./pages/dd/DdDocuments"));
const DdDocDetail = lazyPage(() => import("./pages/dd/DdDocDetail"));
const DdChallans = lazyPage(() => import("./pages/dd/DdChallans"));
const DdChallanNew = lazyPage(() => import("./pages/dd/DdChallanNew"));
const DdChallanDetail = lazyPage(() => import("./pages/dd/DdChallanDetail"));
const DdPettyCash = lazyPage(() => import("./pages/dd/DdPettyCash"));
const DdSettings = lazyPage(() => import("./pages/dd/DdSettings"));

function HomeRoute() {
  const { has } = useAuth();
  if (has("crm_dashboard.view")) return <Dashboard />;
  const dest = firstAllowedPath(has);
  if (dest !== "/home") return <Navigate to={dest} replace />;
  return (
    <div className="max-w-md mx-auto text-center py-20 text-slate-500" data-testid="no-modules">
      No modules have been given to your login yet. Please ask the administrator.
    </div>
  );
}

function RootGate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#070707]"><span className="text-slate-500 text-sm">Loading…</span></div>;
  if (user) return <Navigate to="/home" replace />;
  return <Landing />;
}

// Direct, shareable Sahara sign-in link (bypasses the FactoryFlow homepage).
function SaharaLoginRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-white"><span className="text-slate-400 text-sm">Loading…</span></div>;
  if (user) return <Navigate to="/home" replace />;
  return <Login />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/portal" element={<WorkspacePortal />} />
      <Route path="/demo" element={<DemoLauncher />} />
      <Route path="/login" element={<Login />} />
      <Route path="/sahara" element={<SaharaLoginRoute />} />
      <Route path="/sahara/login" element={<SaharaLoginRoute />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/operator" element={<OperatorLogin />} />
      <Route path="/operator/jobs" element={<OperatorJobs />} />
      <Route path="/operator/jobs/:id" element={<OperatorJobExecution />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route element={<PermGuard />}>
          <Route path="/home" element={<HomeRoute />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/crm-cleanup" element={<CRMCleanup />} />
          <Route path="/mes/planning" element={<MESPlanning />} />
          <Route path="/mes/employees" element={<EmployeeMaster />} />
          <Route path="/mes/approvals" element={<MesApprovals />} />
          <Route path="/mes/register" element={<MesRegister />} />
          <Route path="/mes/dashboard" element={<MesDashboard />} />
          <Route path="/mes/finished-goods" element={<FinishedGoods />} />
          <Route path="/mes/transfers" element={<StockTransfers />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/compound-costing" element={<CompoundCosting />} />
          <Route path="/product-costing" element={<ProductCosting />} />
          <Route path="/enquiries" element={<Enquiries />} />
          <Route path="/quotes/new" element={<QuoteGenerator />} />
          <Route path="/quotes" element={<QuoteRegister />} />
          <Route path="/pis" element={<PIRegister />} />
          <Route path="/tax-invoices" element={<TaxInvoices />} />
          <Route path="/sales-orders" element={<SalesOrders />} />
          <Route path="/sales-orders/:id" element={<SalesOrderDetail />} />
          <Route path="/email-drafts" element={<EmailDrafts />} />
          <Route path="/tally-pairing" element={<TallyPairing />} />
          <Route path="/order-tracking" element={<OrderTracking />} />
          <Route path="/order-tracking/:id" element={<OrderTrackingDetail />} />
          <Route path="/work-orders" element={<WorkOrders />} />
          <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
          <Route path="/scan/die/:id" element={<ScanDie />} />
          <Route path="/box/:uid" element={<BoxPage />} />
          <Route path="/dispatch/scan-load" element={<ScanLoad />} />
          <Route path="/dispatch/scan-load/:did" element={<ScanLoad />} />
          <Route path="/dispatch/boxes" element={<BoxTrace />} />
          <Route path="/products/sticker-photos" element={<StickerPhotos />} />
          <Route path="/customers/:id/item-names" element={<CustomerItemNames />} />
          <Route path="/production" element={<ProductionDashboard />} />
          <Route path="/production/plant/:name" element={<PlantDashboard />} />
          <Route path="/recipe-master" element={<RecipeMaster />} />
          <Route path="/raw-materials" element={<RawMaterialMaster />} />
          <Route path="/mould-master" element={<MouldMaster />} />
          <Route path="/machine-master" element={<MachineMaster />} />
          <Route path="/machines/:id" element={<MachineDetail />} />
          <Route path="/maintenance" element={<MaintenanceDashboard />} />
          <Route path="/breakdowns" element={<Breakdowns />} />
          <Route path="/pm-schedule" element={<PMSchedule />} />
          <Route path="/procurement" element={<ProcurementDashboard />} />
          <Route path="/grn" element={<MaterialReceipts />} />
          <Route path="/grn/new" element={<NewGRN />} />
          <Route path="/grn/:id" element={<GRNDetail />} />
          <Route path="/grn/:id/verify" element={<NewGRN />} />
          <Route path="/vendors" element={<Vendors />} />
          <Route path="/rm-stock" element={<RMStock />} />
          <Route path="/inventory/raw-materials" element={<RawMaterialInventory variant="production" />} />
          <Route path="/inventory/compound-production" element={<ProductionRegister />} />
          <Route path="/inventory/compound-production/new" element={<CompoundProductionEntry />} />
          <Route path="/inventory/compound-production/:id/edit" element={<CompoundProductionEntry />} />
          <Route path="/inventory/physical-stock" element={<PhysicalStock />} />
          <Route path="/purchase/stock-dashboard" element={<InventoryDashboard />} />
          <Route path="/purchase/raw-materials" element={<RawMaterialInventory variant="purchase" />} />
          <Route path="/purchase/ledger" element={<InventoryLedger />} />
          <Route path="/purchase/transfers" element={<StockTransfers />} />
          <Route path="/purchase/recommendations" element={<PurchaseRecommendations />} />
          <Route path="/data-import" element={<ImportWizard />} />
          <Route path="/production-planning" element={<ProductionPlanning />} />
          <Route path="/compound-batches" element={<CompoundBatches />} />
          <Route path="/production-jobs" element={<ProductionJobs />} />
          <Route path="/quality-control" element={<ComingSoon title="Quality Control" phase="Phase 2C" description="In-process and final QC inspection." />} />
          <Route path="/finishing-challans" element={<FcChallanList />} />
          <Route path="/finishing-challans/new" element={<FcChallanForm />} />
          <Route path="/finishing-challans/reports" element={<FcReports />} />
          <Route path="/finishing-challans/masters" element={<FcMasters />} />
          <Route path="/finishing-challans/:id" element={<FcChallanDetail />} />
          <Route path="/finishing-challans/:id/edit" element={<FcChallanForm />} />
          <Route path="/finishing-challans/:id/inward" element={<FcInwardForm />} />
          <Route path="/finishing-challans/:id/inward/:iid" element={<FcInwardForm />} />
          <Route path="/qc" element={<QcHome />} />
          <Route path="/qc/dispatch/:id" element={<QcDispatch />} />
          <Route path="/qc/inspections/:id" element={<QcInspection />} />
          <Route path="/qc/register" element={<QcRegister />} />
          <Route path="/qc/settings" element={<QcSettings />} />
          <Route path="/qc/dashboard" element={<QcDashboard />} />
          <Route path="/qc/night" element={<QcNightReview />} />
          <Route path="/qc/patrol" element={<QcPatrolBoard />} />
          <Route path="/qc/patrol/:jobId" element={<QcPatrolForm />} />
          <Route path="/qc/fpa" element={<QcStageList kind="fpa" />} />
          <Route path="/qc/fpa/:id" element={<QcStageForm kind="fpa" />} />
          <Route path="/qc/final" element={<QcStageList kind="final" />} />
          <Route path="/qc/final/:id" element={<QcStageForm kind="final" />} />
          <Route path="/qc/templates" element={<QcTemplates />} />
          <Route path="/qc/templates/new" element={<QcTemplateEdit />} />
          <Route path="/qc/templates/:code" element={<QcTemplateEdit />} />
          <Route path="/qc/templates/:code/v/:version" element={<QcTemplateEdit />} />
          <Route path="/capa" element={<CapaList />} />
          <Route path="/capa/new" element={<CapaNew />} />
          <Route path="/capa/templates" element={<CapaTemplates />} />
          <Route path="/capa/templates/new" element={<CapaTemplateEdit />} />
          <Route path="/capa/templates/:code" element={<CapaTemplateEdit />} />
          <Route path="/capa/:id" element={<CapaDetail />} />
          <Route path="/doc-dispatch" element={<DdHome />} />
          <Route path="/doc-dispatch/upload" element={<DdUpload />} />
          <Route path="/doc-dispatch/documents" element={<DdDocuments />} />
          <Route path="/doc-dispatch/documents/:id" element={<DdDocDetail />} />
          <Route path="/doc-dispatch/documents/:id/edit" element={<DdUpload />} />
          <Route path="/doc-dispatch/challans" element={<DdChallans />} />
          <Route path="/doc-dispatch/challans/new" element={<DdChallanNew />} />
          <Route path="/doc-dispatch/challans/:id" element={<DdChallanDetail />} />
          <Route path="/doc-dispatch/petty-cash" element={<DdPettyCash />} />
          <Route path="/doc-dispatch/settings" element={<DdSettings />} />
          <Route path="/finishing" element={<ComingSoon title="Finishing" phase="Phase 2D" description="In-house and outsourced finishing." />} />
          <Route path="/dispatch" element={<Dispatch />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/accounts/einvoice" element={<EInvoiceQueue />} />
          <Route path="/ai/product-finder" element={<AIProductFinder />} />
          <Route path="/ai/weight-calculator" element={<AIWeightCalculator />} />
          <Route path="/ai/mould-cost-estimator" element={<AIToolingEngineer />} />
          <Route path="/tooling" element={<ToolingDashboard />} />
          <Route path="/tooling/requests/:id" element={<ToolingRequestDetail />} />
          <Route path="/purchase" element={<PurchaseIndents />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/followups" element={<Followups />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notes" element={<Notes />} />
          <Route path="/settings" element={<Settings />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <span className="text-slate-400 text-sm">Loading…</span>
    </div>
  );
}

function App() {
  useEffect(() => { prefetchPages(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <AppRouter />
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
